/**
 * RedNote notes, played in a lightbox.
 *
 * RedNote has no embed player, so these are the notes' own videos hosted on Shopify
 * Files, played in the same GLightbox modal and 9:16 frame as the TikTok clips
 * (.beeul-tok-player, styled in beeul.css). All the section's notes open as one set, so
 * a visitor can swipe or arrow between them.
 *
 * Only the slide on screen plays: the others are paused, and a close pauses everything
 * at once rather than when the fade-out finishes. Which slide is on screen is read from
 * the class GLightbox puts on it, not from its slide_changed event, which fires only
 * once the slide animation has run.
 *
 * Nothing loads until the first tap, and each video only buffers once it is on screen.
 */
function loadLightbox() {
  if (window.GLightbox) return Promise.resolve(window.GLightbox);
  window.__beeulLightbox ||= new Promise((resolve) => {
    const css = document.createElement('link');
    css.rel = 'stylesheet';
    css.href = 'https://cdn.jsdelivr.net/npm/glightbox@3.3.1/dist/css/glightbox.min.css';
    document.head.appendChild(css);

    const js = document.createElement('script');
    js.src = 'https://cdn.jsdelivr.net/npm/glightbox@3.3.1/dist/js/glightbox.min.js';
    js.onload = () => resolve(window.GLightbox || null);
    js.onerror = () => resolve(null);
    document.head.appendChild(js);
  });
  return window.__beeulLightbox;
}

function escapeAttr(value) {
  return String(value || '').replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
}

function slideFor(card) {
  const src = escapeAttr(card.dataset.rednoteVideo);
  const poster = escapeAttr(card.querySelector('img')?.currentSrc || card.querySelector('img')?.src);
  const label = escapeAttr(card.getAttribute('aria-label'));
  return {
    content: `<div class="beeul-tok-player"><video src="${src}" poster="${poster}" aria-label="${label}" controls playsinline preload="none"></video></div>`,
    width: 'auto',
    height: 'auto',
  };
}

/** Starts with sound if the browser allows it, and muted if it does not. */
function start(video) {
  if (!video.paused) return;
  const attempt = video.play();
  if (!attempt || !attempt.catch) return;
  attempt.catch(() => {
    // The tap that opened the modal no longer counts as a gesture once the library has
    // loaded, and a browser may then refuse sound. Muted playback is always allowed;
    // the controls are there to turn it up.
    video.muted = true;
    video.play().catch(() => {});
  });
}

function syncVideos(container) {
  if (container.dataset.beeulClosing) return;
  container.querySelectorAll('.beeul-tok-player video').forEach((video) => {
    const onScreen = video.closest('.gslide')?.classList.contains('current');
    if (onScreen) start(video);
    else if (!video.paused) video.pause();
  });
}

function pauseAll() {
  document.querySelectorAll('.glightbox-container').forEach((container) => {
    container.dataset.beeulClosing = 'true';
  });
  document.querySelectorAll('.beeul-tok-player video').forEach((video) => video.pause());
}

// Pause the moment a close is asked for: the close button, a tap outside the video, or
// Esc. Caught in the capture phase, since GLightbox binds these to its own close.
document.addEventListener(
  'click',
  (event) => {
    const inModal = event.target.closest('.glightbox-container');
    if (inModal && !event.target.closest('.beeul-tok-player, .gnext, .gprev')) pauseAll();
  },
  true
);

document.addEventListener(
  'keydown',
  (event) => {
    if (event.key === 'Escape' && document.documentElement.classList.contains('glightbox-open')) pauseAll();
  },
  true
);

document.addEventListener('click', async (event) => {
  const card = event.target.closest('.beeul-rn__card[data-rednote-video]');
  if (!card) return;

  const row = card.closest('.beeul-rn__row');
  const cards = row ? [...row.querySelectorAll('.beeul-rn__card[data-rednote-video]')] : [card];
  const index = cards.indexOf(card);

  card.setAttribute('aria-busy', 'true');
  const lightbox = await loadLightbox();
  card.removeAttribute('aria-busy');

  // Without the lightbox, the video file itself is still watchable.
  if (!lightbox) {
    window.open(card.dataset.rednoteVideo, '_blank', 'noopener');
    return;
  }

  const box = lightbox({
    elements: cards.map(slideFor),
    startAt: index,
    loop: false,
    touchNavigation: true,
    keyboardNavigation: true,
    zoomable: false,
    draggable: true,
    openEffect: 'fade',
    closeEffect: 'fade',
    slideEffect: 'slide',
  });

  let watcher = null;
  const newest = () => {
    const containers = document.querySelectorAll('.glightbox-container');
    return containers[containers.length - 1];
  };

  box.on('open', () => {
    const container = newest();
    if (!container) return;
    delete container.dataset.beeulClosing;
    syncVideos(container);
    watcher = new MutationObserver(() => {
      if (!document.documentElement.classList.contains('glightbox-open')) {
        pauseAll();
        watcher?.disconnect();
        return;
      }
      syncVideos(container);
    });
    watcher.observe(container, { subtree: true, childList: true, attributes: true, attributeFilter: ['class'] });
    watcher.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
  });

  box.on('slide_after_load', () => {
    const container = newest();
    if (container) syncVideos(container);
  });

  box.on('close', () => {
    pauseAll();
    watcher?.disconnect();
    card.focus({ preventScroll: true });
  });

  box.open();
});
